import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Search } from 'lucide-react';
import { icmsPorAliquota } from '../taxCodes';
import {
  StyledCard, FormGrid, StyledFormGroup, TooltipWrapper, InfoIcon,
  NcmTag, RegionButton, DropdownList, DropdownItem
} from './style';

const REGION_OPTIONS = [
  { value: 'CENTRO_OESTE', label: 'Centro-Oeste' },
  { value: 'NORDESTE', label: 'Nordeste' },
  { value: 'NORTE', label: 'Norte' },
  { value: 'SUDESTE', label: 'Sudeste' },
  { value: 'SUL', label: 'Sul' },
];

const LabelWithInfo = ({ label, tooltip }: { label: string, tooltip: string }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <label style={{ marginBottom: 0 }}>{label}</label>
    <TooltipWrapper data-tooltip={tooltip}>
      <InfoIcon>i</InfoIcon>
    </TooltipWrapper>
  </div>
);

export const ScenarioCard = ({ scenario, index, updateScenario, removeScenario, allStates, taxCodes, classTribOptions }: any) => {
  const [stateSearch, setStateSearch] = useState("");
  const [isStateListOpen, setIsStateListOpen] = useState(false);
  const [classTribSearch, setClassTribSearch] = useState("");
  const [isClassTribOpen, setIsClassTribOpen] = useState(false);
  const classTribRef = useRef<HTMLDivElement>(null);

  const codes = taxCodes ?? { cstIcms: [], csosn: [], cstIbsCbs: [] };
  const aliquotaEditavel = icmsPorAliquota({ cstIcms: codes.cstIcms ?? [], csosn: codes.csosn ?? [] }, scenario.cstICMS);
  const selectedIbsCbs = (codes.cstIbsCbs ?? []).find((c: any) => c.code === scenario.cstIBS_CBS);
  const classTribFiltered = selectedIbsCbs
    ? (classTribOptions ?? []).filter((c: any) => c.cstIbsCbsId === selectedIbsCbs.id)
    : [];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (classTribRef.current && !classTribRef.current.contains(e.target as Node)) {
        setIsClassTribOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredClassTrib = classTribFiltered.filter((opt: any) => {
    if (!classTribSearch) return true;
    const s = classTribSearch.toLowerCase();
    return (
      String(opt.code || '').toLowerCase().includes(s) ||
      String(opt.description || '').toLowerCase().includes(s)
    );
  });

  const selectedClassTribOption = classTribFiltered.find(
    (opt: any) => opt.code === scenario.classificacaoTributaria
  );

  return (
    <StyledCard style={{ border: '1px solid #eee', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <NcmTag style={{ border: 'none', fontWeight: 'bold', textTransform: 'uppercase', background: '#e3f2fd' }}>
          Cenário {index + 1}
        </NcmTag>
        {index > 0 && (
          <div onClick={() => removeScenario(scenario.id)} style={{ cursor: 'pointer', background: '#FEECEC', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={14} color="#F75A68" />
          </div>
        )}
      </div>

      <FormGrid cols={2}>
        <StyledFormGroup>
          <LabelWithInfo label="CST ICMS" tooltip="Código de Situação Tributária do ICMS" />
          <select value={scenario.cstICMS} onChange={(e) => {
              const novo = e.target.value;
              updateScenario(scenario.id, 'cstICMS', novo);
              if (!icmsPorAliquota({ cstIcms: codes.cstIcms ?? [], csosn: codes.csosn ?? [] }, novo)) {
                updateScenario(scenario.id, 'aliquotaICMS', '');
                updateScenario(scenario.id, 'baseDeCalculo', '');
              }
            }}>
            <option value="">Selecione</option>
            {(codes.cstIcms ?? [])
              .filter((opt: any) => !opt.substituicaoTributaria || opt.code === scenario.cstICMS)
              .map((opt: any) => (
                <option key={opt.code} value={opt.code}>
                  {opt.code} — {opt.description}{opt.substituicaoTributaria ? ' (não suportado na emissão)' : ''}
                </option>
              ))}
          </select>
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Alíquota ICMS (%)" tooltip="Alíquota interna ou interestadual" />
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            value={aliquotaEditavel ? scenario.aliquotaICMS : ''}
            disabled={!aliquotaEditavel}
            onChange={(e) => updateScenario(scenario.id, 'aliquotaICMS', e.target.value === '' ? '' : Number(e.target.value))}
          />
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Base de cálculo (%)" tooltip="Percentual da base de cálculo" />
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            value={aliquotaEditavel ? scenario.baseDeCalculo : ''}
            disabled={!aliquotaEditavel}
            onChange={(e) => updateScenario(scenario.id, 'baseDeCalculo', e.target.value === '' ? '' : Number(e.target.value))}
          />
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Diferencial de alíquota" tooltip="Escolha se aplica DIFAL ou Operação Normal" />
          <select
            value={
              scenario.tipoDiferencial !== undefined
                ? scenario.tipoDiferencial
                : scenario.diferencialAliquota
                ? 'difal'
                : ''
            }
            onChange={(e) => {
              const val = e.target.value;
              updateScenario(scenario.id, 'tipoDiferencial', val);
              updateScenario(scenario.id, 'diferencialAliquota', val === 'difal');
            }}
          >
            <option value="">Selecione</option>
            <option value="normal">Não se aplica</option>
            <option value="difal">Aplicar DIFAL</option>
          </select>
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="CST IBS/CBS" tooltip="Novo imposto da reforma tributária" />
          <select
            value={scenario.cstIBS_CBS}
            onChange={(e) => {
              updateScenario(scenario.id, 'cstIBS_CBS', e.target.value);
              updateScenario(scenario.id, 'classificacaoTributaria', '');
            }}
          >
            <option value="">Selecione</option>
            {(codes.cstIbsCbs ?? []).map((opt: any) => (
              <option key={opt.code} value={opt.code}>{opt.code} — {opt.description}</option>
            ))}
          </select>
        </StyledFormGroup>

        <StyledFormGroup style={{ position: 'relative', zIndex: isClassTribOpen ? 100 : 1 }}>
          <LabelWithInfo label="Classificação tributária (cClassTrib)" tooltip="Código pareado ao CST IBS/CBS — obrigatório para emitir" />
          <div ref={classTribRef} style={{ position: 'relative', width: '100%' }}>
            <div
              onClick={() => {
                if (selectedIbsCbs) {
                  setIsClassTribOpen(!isClassTribOpen);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 15px',
                border: '1px solid #ddd',
                borderRadius: '100px',
                fontSize: '14px',
                height: '40px',
                boxSizing: 'border-box',
                backgroundColor: !selectedIbsCbs ? '#c4c4c4' : '#ebebeb',
                color: !selectedIbsCbs ? '#696969' : '#333',
                cursor: !selectedIbsCbs ? 'not-allowed' : 'pointer',
                userSelect: 'none',
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  paddingRight: '10px',
                  color: selectedClassTribOption ? '#333' : '#999',
                }}
              >
                {selectedClassTribOption
                  ? `${selectedClassTribOption.code} — ${selectedClassTribOption.description}`
                  : selectedIbsCbs
                  ? 'Selecione'
                  : 'Selecione o CST IBS/CBS antes'}
              </span>
              <ChevronDown size={18} color={!selectedIbsCbs ? '#999' : '#666'} style={{ flexShrink: 0 }} />
            </div>

            {isClassTribOpen && selectedIbsCbs && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  width: '100%',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  border: '1px solid #e0e0e0',
                  maxHeight: '280px',
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 9999,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #ddd', borderRadius: '20px', padding: '4px 10px' }}>
                    <Search size={14} color="#999" />
                    <input
                      type="text"
                      placeholder="Pesquisar código ou descrição..."
                      value={classTribSearch}
                      onChange={(e) => setClassTribSearch(e.target.value)}
                      autoFocus
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        fontSize: '13px',
                        background: 'transparent',
                        padding: '2px 0',
                        color: '#333',
                      }}
                    />
                    {classTribSearch && (
                      <X size={14} color="#999" style={{ cursor: 'pointer' }} onClick={() => setClassTribSearch('')} />
                    )}
                  </div>
                </div>

                <ul style={{ listStyle: 'none', margin: 0, padding: 0, overflowY: 'auto', flex: 1 }}>
                  {scenario.classificacaoTributaria && (
                    <li
                      onMouseDown={() => {
                        updateScenario(scenario.id, 'classificacaoTributaria', '');
                        setIsClassTribOpen(false);
                        setClassTribSearch('');
                      }}
                      style={{
                        padding: '8px 14px',
                        fontSize: '12px',
                        color: '#d32f2f',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        fontWeight: 500,
                      }}
                    >
                      × Limpar seleção
                    </li>
                  )}
                  {filteredClassTrib.length > 0 ? (
                    filteredClassTrib.map((opt: any) => {
                      const isSelected = scenario.classificacaoTributaria === opt.code;
                      return (
                        <li
                          key={opt.code}
                          onMouseDown={() => {
                            updateScenario(scenario.id, 'classificacaoTributaria', opt.code);
                            setIsClassTribOpen(false);
                            setClassTribSearch('');
                          }}
                          style={{
                            padding: '10px 14px',
                            fontSize: '13px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f5f5f5',
                            lineHeight: '1.4',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            backgroundColor: isSelected ? '#e6f3fa' : '#ffffff',
                            color: isSelected ? '#0085CC' : '#333',
                            transition: 'background-color 0.15s',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = '#ffffff';
                          }}
                        >
                          <strong style={{ color: isSelected ? '#0085CC' : '#111' }}>{opt.code}</strong> — {opt.description}
                        </li>
                      );
                    })
                  ) : (
                    <li style={{ padding: '14px', fontSize: '13px', color: '#999', textAlign: 'center' }}>
                      Nenhuma classificação encontrada
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Alíquota CBS (%)" tooltip="Alíquota da CBS" />
          <input type="number" value={scenario.aliquotaCBS} onChange={(e) => updateScenario(scenario.id, 'aliquotaCBS', Number(e.target.value))} />
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Alíquota IBS (%)" tooltip="Alíquota da IBS" />
          <input type="number" value={scenario.aliquotaIBS} onChange={(e) => updateScenario(scenario.id, 'aliquotaIBS', Number(e.target.value))} />
        </StyledFormGroup>
      </FormGrid>

      <div style={{ marginTop: '25px' }}>
        <label style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '10px' }}>Aplicar a:</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {REGION_OPTIONS.map((region) => (
            <RegionButton
              key={region.value}
              $active={scenario.regions?.includes(region.value)}
              onClick={() => {
                const currentRegions = scenario.regions || [];
                const newRegions = currentRegions.includes(region.value)
                  ? currentRegions.filter((r: string) => r !== region.value)
                  : [...currentRegions, region.value];
                updateScenario(scenario.id, 'regions', newRegions);
              }}
            >
              {region.label}
            </RegionButton>
          ))}

          <RegionButton
            $active={scenario.applyToSameState}
            onClick={() => updateScenario(scenario.id, 'applyToSameState', !scenario.applyToSameState)}
          >
            Mesmo estado
          </RegionButton>

          <RegionButton
            $active={scenario.showStateInput}
            onClick={() => updateScenario(scenario.id, 'showStateInput', !scenario.showStateInput)}
          >
            Por estado
          </RegionButton>

          {scenario.showStateInput && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#333', marginLeft: '8px' }}>
                Estados específicos:
              </label>

              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '6px', background: '#ebebeb',
                padding: '8px 15px', borderRadius: '100px', width: '350px',
                minHeight: '42px', alignItems: 'center', border: '1px solid #ddd'
              }}>
                {allStates.filter((s: any) => scenario.stateIds?.includes(s.id)).map((state: any) => (
                  <span key={state.id} style={{
                    background: '#fff', padding: '2px 10px', borderRadius: '12px',
                    fontSize: '12px', fontWeight: 'bold', display: 'flex',
                    alignItems: 'center', gap: '5px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    {state.uf}
                    <button
                      type="button"
                      onClick={() => updateScenario(scenario.id, 'stateIds', scenario.stateIds.filter((id: number) => id !== state.id))}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ff4d4d', fontSize: '14px' }}
                    >
                      ×
                    </button>
                  </span>
                ))}

                <input
                  type="text"
                  placeholder={scenario.stateIds?.length > 0 ? "" : "Buscar estado..."}
                  value={stateSearch}
                  onFocus={() => setIsStateListOpen(true)}
                  onClick={() => setIsStateListOpen(true)}
                  onChange={(e) => setStateSearch(e.target.value)}
                  onBlur={() => {
                    setTimeout(() => {
                      setIsStateListOpen(false);
                      setStateSearch("");
                    }, 200);
                  }}
                  style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: '14px', flex: 1, minWidth: '80px' }}
                />
              </div>

              {isStateListOpen && (
                <DropdownList style={{ width: '350px' }}>
                  {allStates
                    .filter((st: any) =>
                      !stateSearch ||
                      st.name.toLowerCase().includes(stateSearch.toLowerCase()) ||
                      st.uf.toLowerCase().includes(stateSearch.toLowerCase())
                    )
                    .map((state: any) => (
                      <DropdownItem
                        key={state.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const currentIds = scenario.stateIds || [];
                          if (!currentIds.includes(state.id)) {
                            updateScenario(scenario.id, 'stateIds', [...currentIds, state.id]);
                          }
                          setStateSearch("");
                        }}
                      >
                        <strong>{state.uf}</strong> — {state.name}
                      </DropdownItem>
                    ))}
                </DropdownList>
              )}
            </div>
          )}
        </div>
      </div>
    </StyledCard>
  );
};
